import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { variant } from "styled-system";

/**
 * Vertical coloured bar that sits next to a SideNav avatar to indicate the
 * team / entity type. Colour is driven by the `badgeVariant` prop, mapped to
 * theme colour tokens via styled-system's `variant`.
 *
 * Use the matching `<TeamTypeBarPlaceholder />` to keep alignment for rows
 * that have no team type (e.g. people / tags in a pinned list).
 */
const TeamTypeBarStyled = styled.div`
  width: 4px;
  height: 22px;
  margin-left: 1px;
  flex-shrink: 0;
  border-radius: 2px;
  ${variant({
    prop: "badgeVariant",
    variants: {
      success: { bg: "successLightest" },
      successPending: { bg: "successEvenLighter" },
      warning: { bg: "warningLightest" },
      danger: { bg: "dangerLightest" },
      primaryLight: { bg: "primaryLightest" },
      primary: { bg: "primaryLighter" },
      primaryDark: { bg: "primaryLight" },
      secondary: { bg: "secondaryEvenLighter" },
      default: { bg: "secondary" },
    },
  })}
`;

const TeamTypeBar = ({ badgeVariant = "default" }) => (
  <TeamTypeBarStyled badgeVariant={badgeVariant} />
);

TeamTypeBar.propTypes = {
  badgeVariant: PropTypes.oneOf([
    "success",
    "successPending",
    "warning",
    "danger",
    "primaryLight",
    "primary",
    "primaryDark",
    "secondary",
    "default",
  ]),
};

/**
 * Same effective horizontal footprint as `<TeamTypeBar />` (4px width +
 * 1px left margin = 5px total) but invisible, used to keep non-team rows
 * aligned with team rows that render a coloured bar.
 */
export const TeamTypeBarPlaceholder = styled.div`
  width: 4px;
  margin-left: 1px;
  flex-shrink: 0;
`;

export default TeamTypeBar;
